import React, { useState } from 'react';
import { useAuth } from '../../shared/hooks';
import { Button, Input, Card } from '../../shared/components';

const LoginScreen: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isRegister, setIsRegister] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [profileType, setProfileType] = useState<'tutor' | 'child'>('tutor');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | 'info'>('info');
  const { login, register, loading, error, resendVerificationEmail, resetPassword, loginWithGoogle } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    
    if (!email || !password) {
      setMessage('Por favor completa todos los campos');
      setMessageType('error');
      return;
    }

    if (isRegister) {
      if (!confirmPassword) {
        setMessage('Por favor confirma tu contraseña');
        setMessageType('error');
        return;
      }

      if (password !== confirmPassword) {
        setMessage('Las contraseñas no coinciden');
        setMessageType('error');
        return;
      }

      const result = await register(email, password, profileType);
      if (result.success) {
        setMessage(result.message || 'Registro exitoso. Por favor verifica tu correo electrónico.');
        setMessageType('success');
        setIsRegister(false);
        setPassword('');
        setConfirmPassword('');
      } else {
        // Verificar si el error es por email ya registrado
        if (result.error && result.error.includes('email-already-in-use')) {
          setMessage('Este correo electrónico ya está registrado. Por favor inicia sesión.');
          setMessageType('info');
          setIsRegister(false);
          setConfirmPassword('');
          // Email y contraseña se preservan automáticamente para facilitar el login
        } else {
          setMessage(result.error || 'Error en el registro');
          setMessageType('error');
        }
      }
    } else {
      const result = await login(email, password);
      if (result.success) {
        // Login exitoso, el hook manejará la redirección
      } else if (result.needsVerification) {
        setMessage(result.error || 'Por favor verifica tu correo electrónico.');
        setMessageType('error');
      } else {
        // Verificar si el error es por usuario no encontrado
        if (result.error && result.error.includes('user-not-found')) {
          setMessage('Esta cuenta no está registrada. Por favor regístrate.');
          setMessageType('info');
          setIsRegister(true);
          setConfirmPassword('');
        } else if (result.error && result.error.includes('invalid-credential')) {
          setMessage('Correo o contraseña incorrectos. Por favor verifica tus credenciales.');
          setMessageType('error');
        } else {
          setMessage(result.error || 'Error al iniciar sesión');
          setMessageType('error');
        }
      }
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    
    if (!email) {
      setMessage('Por favor ingresa tu correo electrónico');
      setMessageType('error');
      return;
    }

    const result = await resetPassword(email);
    if (result.success) {
      setMessage(result.message || 'Email de recuperación enviado');
      setMessageType('success');
      setShowResetPassword(false);
    } else {
      setMessage(result.error || 'Error al enviar email de recuperación');
      setMessageType('error');
    }
  };

  const handleResendVerification = async () => {
    const result = await resendVerificationEmail();
    if (result.success) {
      setMessage(result.message || 'Email de verificación reenviado');
      setMessageType('success');
    } else {
      setMessage(result.error || 'Error al reenviar email');
      setMessageType('error');
    }
  };

  const handleGoogleLogin = async () => {
    setMessage('');
    const result = await loginWithGoogle();
    if (result.success) {
      setMessage(result.message || 'Inicio de sesión con Google exitoso');
      setMessageType('success');
    } else {
      setMessage(result.error || 'Error al iniciar sesión con Google');
      setMessageType('error');
    }
  };

  if (showResetPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 p-4 sm:p-6 md:p-8 flex items-center justify-center">
        <Card className="w-full max-w-md p-6 sm:p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
              Recuperar Contraseña
            </h1>
            <p className="text-gray-600 text-sm sm:text-base">
              Ingresa tu correo para recibir instrucciones
            </p>
          </div>

          <form onSubmit={handleResetPassword} className="space-y-4">
            <Input
              type="email"
              label="Correo electrónico"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@email.com"
              required
            />

            {message && (
              <div className={`px-4 py-3 rounded-lg text-sm ${
                messageType === 'success' ? 'bg-green-50 border border-green-200 text-green-600' :
                messageType === 'error' ? 'bg-red-50 border border-red-200 text-red-600' :
                'bg-blue-50 border border-blue-200 text-blue-600'
              }`}>
                {message}
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
            >
              {loading ? 'Enviando...' : 'Enviar Instrucciones'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => {
                setShowResetPassword(false);
                setMessage('');
              }}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Volver al inicio de sesión
            </button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 p-4 sm:p-6 md:p-8 flex items-center justify-center">
      <Card className="w-full max-w-md p-6 sm:p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
            {isRegister ? 'Crear Cuenta' : 'Iniciar Sesión'}
          </h1>
          <p className="text-gray-600 text-sm sm:text-base">
            {isRegister ? 'Únete a Calmi para comenzar' : 'Bienvenido de nuevo'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            type="email"
            label="Correo electrónico"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tu@email.com"
            required
          />

          <Input
            type="password"
            label="Contraseña"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
            minLength={6}
          />

          {isRegister && (
            <>
              <Input
                type="password"
                label="Confirmar contraseña"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
              />
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de perfil
                </label>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setProfileType('tutor')}
                    className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                      profileType === 'tutor'
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <div className="text-3xl mb-2">👨‍👩‍👧‍👦</div>
                    <div className="font-semibold text-gray-800">Tutor</div>
                    <div className="text-xs text-gray-600">Para padres/guardianes</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setProfileType('child')}
                    className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                      profileType === 'child'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="text-3xl mb-2">👶</div>
                    <div className="font-semibold text-gray-800">Menor</div>
                    <div className="text-xs text-gray-600">Para niños/niñas</div>
                  </button>
                </div>
              </div>
            </>
          )}

          {message && (
            <div className={`px-4 py-3 rounded-lg text-sm ${
              messageType === 'success' ? 'bg-green-50 border border-green-200 text-green-600' :
              messageType === 'error' ? 'bg-red-50 border border-red-200 text-red-600' :
              'bg-blue-50 border border-blue-200 text-blue-600'
            }`}>
              {message}
              {messageType === 'error' && message.includes('verifica tu correo') && !message.includes('incorrectos') && (
                <button
                  type="button"
                  onClick={handleResendVerification}
                  className="ml-2 underline font-medium"
                >
                  Reenviar email
                </button>
              )}
            </div>
          )}

          {!isRegister && (
            <div className="text-right">
              <button
                type="button"
                onClick={() => setShowResetPassword(true)}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={loading}
          >
            {loading ? 'Procesando...' : (isRegister ? 'Registrarse' : 'Iniciar Sesión')}
          </Button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">O continúa con</span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-full hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            <span className="text-gray-700 font-medium">Google</span>
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-gray-600 text-sm">
            {isRegister ? '¿Ya tienes cuenta?' : '¿No tienes cuenta?'}
            <button
              type="button"
              onClick={() => {
                setIsRegister(!isRegister);
                setMessage('');
              }}
              className="ml-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              {isRegister ? 'Inicia sesión' : 'Regístrate'}
            </button>
          </p>
        </div>
      </Card>
    </div>
  );
};

export default LoginScreen;
